/**
 * Cart Count Updater
 * Updates the cart count bubble in the header when cart changes
 */

class CartCountUpdater {
  // Cache for product metafields to avoid repeated API calls
  static productMetafieldsCache = new Map();

  static async updateCartCount(cartData) {
    if (!cartData) return;

    // Find all cart icon bubbles in the header
    const cartIcons = document.querySelectorAll('#cart-icon-bubble, .cart-icon-bubble');

    // Update all cart icons in parallel
    const updatePromises = Array.from(cartIcons).map((cartIcon) =>
      this.updateSingleCartIcon(cartIcon, cartData)
    );

    await Promise.all(updatePromises);
  }

  static async updateSingleCartIcon(cartIcon, cartData) {
    if (!cartIcon || !cartData) return;

    // Calculate total item count (including bundle components)
    const totalCount = await this.calculateTotalItemCount(cartData);

    // Update or create cart count bubble
    let cartCountBubble = cartIcon.querySelector('.cart-count-bubble');

    if (totalCount > 0) {
      if (!cartCountBubble) {
        // Create cart count bubble if it doesn't exist
        cartCountBubble = document.createElement('div');
        cartCountBubble.className = 'cart-count-bubble';
        cartIcon.appendChild(cartCountBubble);
      }

      // Update the bubble content
      cartCountBubble.innerHTML = `
        ${totalCount < 100 ? `<span aria-hidden="true">${totalCount}</span>` : ''}
        <span class="visually-hidden">${totalCount} items in cart</span>
      `;

      // Show the bubble
      cartCountBubble.style.display = 'block';
      
      // Announce cart update to screen reader
      if (window.announceToScreenReader) {
        window.announceToScreenReader(`Cart updated: ${totalCount} items`);
      }
    } else {
      // Hide the bubble if cart is empty
      if (cartCountBubble) {
        cartCountBubble.style.display = 'none';
      }
    }
  }

  static async fetchProductMetafields(handle) {
    if (!handle) return null;

    // Check cache first
    if (this.productMetafieldsCache.has(handle)) {
      return this.productMetafieldsCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}?view=metafields`);
      if (response.ok) {
        const metafieldsData = await response.json();

        // Cache the data
        this.productMetafieldsCache.set(handle, metafieldsData);

        return metafieldsData;
      }
    } catch (error) {
      console.error(`Failed to fetch metafields for ${handle}:`, error);
    }

    return null;
  }

  static async fetchAllCartItemsMetafields(cartData) {
    if (!cartData || !cartData.items) return;


    // Create array of promises to fetch all metafields in parallel
    const fetchPromises = cartData.items.map(async (item) => {
      // Skip if not a product or if we already have the data
      if (item.product_type === 'membership' || !item.handle) return;

      const metafieldsData = await this.fetchProductMetafields(item.handle);
      if (metafieldsData) {
        // Attach metafields to the item
        item.metafields = metafieldsData.metafields;
        item.is_bundle = metafieldsData.is_bundle;
        item.pack_size = metafieldsData.pack_size;
      }
    });

    // Wait for all fetches to complete
    await Promise.all(fetchPromises);
  }

  static async calculateTotalItemCount(cartData) {
    if (!cartData || !cartData.items) return 0;

    // Fetch metafields for all items first
    await this.fetchAllCartItemsMetafields(cartData);

    let totalCount = 0;

    cartData.items.forEach((item) => {
      // Skip membership items
      if (item.product_type === 'membership') {
        return;
      }

      // Use metafields data first (most accurate)
      if (item.is_bundle && item.pack_size) {
        totalCount += item.pack_size * (item.quantity || 1);
      }
      // Fallback to custom bundle properties
      else if (item.properties && item.properties._pack_size) {
        const packSize = parseInt(item.properties._pack_size) || 1;
        totalCount += packSize * (item.quantity || 1);
      }
      // Check for bundle parts property
      else if (item.properties && item.properties._bundle_parts) {
        try {
          // Unescape HTML entities and parse JSON
          const unescaped = item.properties._bundle_parts
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&');

          const bundleParts = JSON.parse(unescaped);
          if (Array.isArray(bundleParts)) {
            const bundleSize = bundleParts.length;
            totalCount += bundleSize * (item.quantity || 1);
          } else {
            totalCount += item.quantity || 0;
          }
        } catch (e) {
          console.error('Failed to parse bundle parts:', e);
          totalCount += item.quantity || 0;
        }
      }
      // Check for native Shopify bundles
      else if (item.item_components && item.item_components.length > 0) {
        // For bundle items, count each component
        let componentCount = 0;
        item.item_components.forEach((component) => {
          componentCount += component.quantity || 1;
        });
        totalCount += componentCount;
      }
      // Regular items
      else {
        totalCount += item.quantity || 0;
      }
    });

    return totalCount;
  }

  static async updateCartIconAsync(cartData) {
    if (!cartData) return;


    // Find all cart icons
    const cartIcons = document.querySelectorAll('#cart-icon-bubble, .cart-icon-bubble');

    // Update all cart icons in parallel
    const updatePromises = Array.from(cartIcons).map(async (cartIcon) => {
      // Update cart icon SVG based on cart state
      const svgWrapper = cartIcon.querySelector('.svg-wrapper');
      if (svgWrapper) {
        if (cartData.item_count > 0) {
          // Cart has items - show filled cart icon
          svgWrapper.innerHTML = this.getCartIconSVG('filled');
        } else {
          // Cart is empty - show empty cart icon
          svgWrapper.innerHTML = this.getCartIconSVG('empty');
        }
      }

      // Update cart count with metafields
      await this.updateSingleCartIcon(cartIcon, cartData);
    });

    await Promise.all(updatePromises);
  }

  // Synchronous version for backward compatibility (fallback without metafields)
  static updateCartIcon(cartData) {
    if (!cartData) return;


    // Try async version first, fallback to sync if it fails
    this.updateCartIconAsync(cartData).catch((error) => {
      console.warn('Failed to update cart with metafields, using fallback:', error);
      this.updateCartIconSync(cartData);
    });
  }

  static updateCartIconSync(cartData) {
    if (!cartData) return;

    // Find all cart icons
    const cartIcons = document.querySelectorAll('#cart-icon-bubble, .cart-icon-bubble');

    cartIcons.forEach((cartIcon) => {
      // Update cart icon SVG based on cart state
      const svgWrapper = cartIcon.querySelector('.svg-wrapper');
      if (svgWrapper) {
        if (cartData.item_count > 0) {
          // Cart has items - show filled cart icon
          svgWrapper.innerHTML = this.getCartIconSVG('filled');
        } else {
          // Cart is empty - show empty cart icon
          svgWrapper.innerHTML = this.getCartIconSVG('empty');
        }
      }

      // Update cart count synchronously (without metafields)
      this.updateSingleCartIconSync(cartIcon, cartData);
    });
  }

  static updateSingleCartIconSync(cartIcon, cartData) {
    if (!cartIcon || !cartData) return;

    // Calculate total item count using fallback method (without metafields)
    const totalCount = this.calculateTotalItemCountSync(cartData);

    // Update or create cart count bubble
    let cartCountBubble = cartIcon.querySelector('.cart-count-bubble');

    if (totalCount > 0) {
      if (!cartCountBubble) {
        // Create cart count bubble if it doesn't exist
        cartCountBubble = document.createElement('div');
        cartCountBubble.className = 'cart-count-bubble';
        cartIcon.appendChild(cartCountBubble);
      }

      // Update the bubble content
      cartCountBubble.innerHTML = `
        ${totalCount < 100 ? `<span aria-hidden="true">${totalCount}</span>` : ''}
        <span class="visually-hidden">${totalCount} items in cart</span>
      `;

      // Show the bubble
      cartCountBubble.style.display = 'block';
    } else {
      // Hide the bubble if cart is empty
      if (cartCountBubble) {
        cartCountBubble.style.display = 'none';
      }
    }
  }

  static calculateTotalItemCountSync(cartData) {
    if (!cartData || !cartData.items) return 0;

    let totalCount = 0;

    cartData.items.forEach((item) => {
      // Skip membership items
      if (item.product_type === 'membership') {
        return;
      }

      // Check for custom bundle properties
      if (item.properties && item.properties._pack_size) {
        const packSize = parseInt(item.properties._pack_size) || 1;
        totalCount += packSize * (item.quantity || 1);
      }
      // Check for native Shopify bundles
      else if (item.item_components && item.item_components.length > 0) {
        item.item_components.forEach((component) => {
          totalCount += (component.quantity || 1);
        });
      }
      // Regular items
      else {
        totalCount += item.quantity || 0;
      }
    });

    return totalCount;
  }

  static getCartIconSVG(type) {
    if (type === 'empty') {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 20" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M4.07125 2.00254C3.89365 2.00216 3.72244 2.02226 3.55976 2.06048C3.33674 1.56893 3.04216 1.12794 2.70711 0.792893C2.13843 0.224221 1.24155 0 0 0V2C0.758448 2 1.1949 2.10911 1.29289 2.20711C1.67086 2.58508 2 3.35306 2 4L2.01005 4.14142L2.98992 11.0005C1.35036 11.0856 0.0801976 12.3588 0.00122858 13.9504L0 15C0.0968212 16.6439 1.36275 17.9085 2.94324 17.9984L3.17026 17.9987C3.58174 19.1646 4.69331 20 6 20C7.30622 20 8.41746 19.1652 8.82929 18H11.1707C11.5825 19.1652 12.6938 20 14 20C15.6569 20 17 18.6569 17 17C17 15.3431 15.6569 14 14 14C12.6938 14 11.5825 14.8348 11.1707 16H8.82929C8.41746 14.8348 7.30622 14 6 14C4.69378 14 3.58254 14.8348 3.17071 16H3C2.49001 15.9694 2.03254 15.5124 1.99821 14.9401L2 14C2.02697 13.4813 2.48131 13.027 3.04955 12.9988L5.02369 12.9993L5.038 13H15.1169L15.2645 12.9532C16.0227 12.7126 16.6296 12.1416 16.917 11.4025L17.0224 11.194L17.3647 10.5159C17.7188 9.81392 18.0729 9.11076 18.418 8.4238C19.2632 6.74107 19.7948 5.67166 19.9125 5.40906C20.5171 4.06045 19.2924 3.02246 18.0407 3.00036L4.07125 2.00254ZM14.7694 11H5.11287C5.04866 10.9822 4.99973 10.9278 4.99015 10.86L4.01026 4.00073L17.8934 4.99493C17.6505 5.49032 17.2089 6.37516 16.6308 7.52609L16.6163 7.55493C16.2863 8.21186 15.9326 8.91405 15.579 9.61507L15.2371 10.2923L15.1076 10.5488L15.0588 10.6622C15.0065 10.8078 14.9028 10.9274 14.7694 11ZM14 18C14.5523 18 15 17.5523 15 17C15 16.4477 14.5523 16 14 16C13.4477 16 13 16.4477 13 17C13 17.5523 13.4477 18 14 18ZM7 17C7 17.5523 6.55228 18 6 18C5.44772 18 5 17.5523 5 17C5 16.4477 5.44772 16 6 16C6.55228 16 7 16.4477 7 17Z" fill="#212121"/>
      </svg>`;
    } else {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 20" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M4.07125 2.00254C3.89365 2.00216 3.72244 2.02226 3.55976 2.06048C3.33674 1.56893 3.04216 1.12794 2.70711 0.792893C2.13843 0.224221 1.24155 0 0 0V2C0.758448 2 1.1949 2.10911 1.29289 2.20711C1.67086 2.58508 2 3.35306 2 4L2.01005 4.14142L2.98992 11.0005C1.35036 11.0856 0.0801976 12.3588 0.00122858 13.9504L0 15C0.0968212 16.6439 1.36275 17.9085 2.94324 17.9984L3.17026 17.9987C3.58174 19.1646 4.69331 20 6 20C7.30622 20 8.41746 19.1652 8.82929 18H11.1707C11.5825 19.1652 12.6938 20 14 20C15.6569 20 17 18.6569 17 17C17 15.3431 15.6569 14 14 14C12.6938 14 11.5825 14.8348 11.1707 16H8.82929C8.41746 14.8348 7.30622 14 6 14C4.69378 14 3.58254 14.8348 3.17071 16H3C2.49001 15.9694 2.03254 15.5124 1.99821 14.9401L2 14C2.02697 13.4813 2.48131 13.027 3.04955 12.9988L5.02369 12.9993L5.038 13H15.1169L15.2645 12.9532C16.0227 12.7126 16.6296 12.1416 16.917 11.4025L17.0224 11.194L17.3647 10.5159C17.7188 9.81392 18.0729 9.11076 18.418 8.4238C19.2632 6.74107 19.7948 5.67166 19.9125 5.40906C20.5171 4.06045 19.2924 3.02246 18.0407 3.00036L4.07125 2.00254ZM14.7694 11H5.11287C5.04866 10.9822 4.99973 10.9278 4.99015 10.86L4.01026 4.00073L17.8934 4.99493C17.6505 5.49032 17.2089 6.37516 16.6308 7.52609L16.6163 7.55493C16.2863 8.21186 15.9326 8.91405 15.579 9.61507L15.2371 10.2923L15.1076 10.5488L15.0588 10.6622C15.0065 10.8078 14.9028 10.9274 14.7694 11ZM14 18C14.5523 18 15 17.5523 15 17C15 16.4477 14.5523 16 14 16C13.4477 16 13 16.4477 13 17C13 17.5523 13.4477 18 14 18ZM7 17C7 17.5523 6.55228 18 6 18C5.44772 18 5 17.5523 5 17C5 16.4477 5.44772 16 6 16C6.55228 16 7 16.4477 7 17Z" fill="#212121"/>
      </svg>`;
    }
  }
}

// Initialize cart count on page load
document.addEventListener('DOMContentLoaded', () => {
  if (window.Shopify && window.Shopify.cart) {
    // Use a small delay to ensure all elements are rendered
    setTimeout(() => {
      CartCountUpdater.updateCartIconAsync(window.Shopify.cart).catch(() => {
        // Fallback to sync version if async fails
        CartCountUpdater.updateCartIconSync(window.Shopify.cart);
      });
    }, 100);
  }
});

// Also initialize when the window loads (fallback)
window.addEventListener('load', () => {
  if (window.Shopify && window.Shopify.cart) {
    CartCountUpdater.updateCartIconAsync(window.Shopify.cart).catch(() => {
      // Fallback to sync version if async fails
      CartCountUpdater.updateCartIconSync(window.Shopify.cart);
    });
  }
});

// Export for use in other scripts
window.CartCountUpdater = CartCountUpdater;
